import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import DesktopPageHeader from "../../../components/DesktopPageHeader";
import { useOwnerRestaurantSelection } from "../../../hooks/useOwnerRestaurantSelection";
import { useAvatar } from "../../../hooks/useAvatar";
import { useUser } from "../../../hooks/useUser";
import { useToast } from "../../../context/ToastContext";
import JwtDecoder from "../../../lib/jwt";
import { getStoredToken } from "../../../services/auth.service";
import { updateTenantRestaurant } from "../../../services/tenantOwnerService";
import { changeAvatar, getUserInfoByUserId, updateProfile } from "../../../services/userService";
import {
  IMAGE_UPLOAD_ACCEPT,
  ALMACEN_IMAGE_RULES,
  USER_AVATAR_IMAGE_RULES,
  readFileAsDataUrl,
  validateImageUpload,
} from "../../../utils/imageUploadValidation";

const SETTINGS_TABS = [
  { id: "cuenta", label: "Cuenta" },
  { id: "almacen", label: "Almacén" },
];

const LEGACY_SETTINGS_TAB_MAP = {
  restaurantes: "almacen",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getProfileErrors = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Ingresa tu nombre.";
  if (!form.lastName.trim()) errors.lastName = "Ingresa tus apellidos.";
  if (!form.email.trim()) {
    errors.email = "Ingresa tu email.";
  } else if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = "Ingresa un email válido.";
  }
  return errors;
};

const getAlmacenErrors = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Ingresa el nombre del almacén.";
  return errors;
};

const splitFullName = (value = "") => {
  const parts = String(value).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { name: "", lastName: "" };
  if (parts.length === 1) return { name: parts[0], lastName: "" };
  return {
    name: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

export default function OwnerSettingsPage() {
  const [activeTab, setActiveTab] = useState("cuenta");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingRestaurant, setIsSavingRestaurant] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileFeedback, setProfileFeedback] = useState({ type: "", message: "" });
  const [almacenFeedback, setAlmacenFeedback] = useState({ type: "", message: "" });
  const [accountAvatarPreview, setAccountAvatarPreview] = useState("");
  const [editingRestaurantForm, setEditingRestaurantForm] = useState({
    id: "",
    name: "",
    address: "",
    placeId: "",
    country: "",
    imagePreview: "",
    imageFile: null,
  });
  const {
    ownerRestaurants,
    ownerRestaurantsLoading,
    setSelectedOwnerRestaurantId,
    selectedOwnerRestaurant,
    reloadOwnerRestaurants,
  } = useOwnerRestaurantSelection();
  const { getAvatar } = useAvatar();
  const { avatar: userAvatar, userId } = useUser();
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();
  const [accountForm, setAccountForm] = useState({
    name: "",
    lastName: "",
    email: "",
  });

  const restoName = selectedOwnerRestaurant?.name || "";
  const restoAddress = selectedOwnerRestaurant?.address || "";
  const restoId = selectedOwnerRestaurant?._id || selectedOwnerRestaurant?.id || "";
  const avatar = selectedOwnerRestaurant?.brand || "";
  const profileAvatar = accountAvatarPreview || userAvatar || avatar;

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      setIsLoadingProfile(true);
      try {
        const profile = userId ? await getUserInfoByUserId(userId) : null;
        if (!mounted) return;

        const splitName = splitFullName(profile?.name || "");
        const name = splitName.name;
        const lastName = splitName.lastName;
        const email = profile?.email || "";
        const nextAvatar = profile?.cover ? getAvatar(profile.cover, profile?.name || "") : "";

        setAccountForm((prev) => ({
          ...prev,
          name,
          lastName,
          email,
        }));
        if (nextAvatar) {
          setAccountAvatarPreview(nextAvatar);
        }
        setIsLoadingProfile(false);
        return;
      } catch (_) {}

      try {
        const token = getStoredToken();
        const payload = token ? JwtDecoder.decode(token) : null;
        if (!mounted) return;

        const payloadName = payload?.name || payload?.firstName || payload?.user?.name || "";
        const splitName = splitFullName(payloadName);
        const name = payload?.firstName || payload?.user?.firstName || splitName.name;
        const lastName = payload?.lastName || payload?.user?.lastName || splitName.lastName;
        const email = payload?.email || payload?.user?.email || "";
        
        setAccountForm((prev) => ({
          ...prev,
          name,
          lastName,
          email,
        }));
      } catch (_) {
      } finally {
        if (mounted) {
          setIsLoadingProfile(false);
        }
      }
    };

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [getAvatar, userId]);

  useEffect(() => {
    if (userAvatar) {
      setAccountAvatarPreview(userAvatar);
    }
  }, [userAvatar]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextTab = params.get("settingsTab");
    const normalizedTab = LEGACY_SETTINGS_TAB_MAP[nextTab] || nextTab;
    const isValidTab = SETTINGS_TABS.some((tab) => tab.id === normalizedTab);

    if (nextTab && !isValidTab) {
      params.delete("settingsTab");
      history.replace({ pathname: location.pathname, search: params.toString() });
      if (activeTab !== "cuenta") {
        setActiveTab("cuenta");
      }
      return;
    }

    if (nextTab && normalizedTab !== nextTab) {
      params.set("settingsTab", normalizedTab);
      history.replace({ pathname: location.pathname, search: params.toString() });
    }

    if (isValidTab && normalizedTab !== activeTab) {
      setActiveTab(normalizedTab);
      return;
    }

    if (!nextTab && activeTab !== "cuenta") {
      setActiveTab("cuenta");
    }
  }, [activeTab, history, location.pathname, location.search]);

  const activeOwnerRestaurants = ownerRestaurants.filter((restaurant) => restaurant?.active !== false);
  const currentAlmacen = selectedOwnerRestaurant || activeOwnerRestaurants[0] || null;
  const profileErrors = getProfileErrors(accountForm);
  const isProfileInvalid = Object.keys(profileErrors).length > 0;
  const almacenErrors = getAlmacenErrors(editingRestaurantForm);
  const isAlmacenInvalid = Object.keys(almacenErrors).length > 0;

  useEffect(() => {
    if (!currentAlmacen) {
      setEditingRestaurantForm({
        id: "",
        name: "",
        address: "",
        placeId: "",
        country: "",
        imagePreview: "",
        imageFile: null,
      });
      return;
    }

    setEditingRestaurantForm({
      id: String(currentAlmacen?._id || currentAlmacen?.id || currentAlmacen?.restoId || currentAlmacen?.place_id || ""),
      name: currentAlmacen?.name || "",
      address: currentAlmacen?.address || "",
      placeId: currentAlmacen?.place_id || "",
      country: currentAlmacen?.country || currentAlmacen?.pais || "",
      imagePreview: currentAlmacen?.brand || "",
      imageFile: null,
    });
  }, [currentAlmacen]);

  const handleFormChange = (field, value) => {
    setAccountForm((prev) => ({ ...prev, [field]: value }));
    setProfileFeedback({ type: "", message: "" });
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = await validateImageUpload(file, USER_AVATAR_IMAGE_RULES);
    if (!validation.ok) {
      toast.error(validation.error);
      event.target.value = "";
      return;
    }

    setIsUploadingAvatar(true);
    try {
      await changeAvatar(file, "avatars");
      const preview = await readFileAsDataUrl(file);
      setAccountAvatarPreview(preview);
      toast.success("Avatar actualizado correctamente.");
    } catch (error) {
      toast.error(error?.message || "No se pudo actualizar el avatar.");
    } finally {
      setIsUploadingAvatar(false);
      event.target.value = "";
    }
  };

  const handleEditRestaurantChange = (field, value) => {
    setEditingRestaurantForm((prev) => ({ ...prev, [field]: value }));
    setAlmacenFeedback({ type: "", message: "" });
  };

  const handleEditRestaurantImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = await validateImageUpload(file, ALMACEN_IMAGE_RULES);
    if (!validation.ok) {
      toast.error(validation.error);
      event.target.value = "";
      return;
    }

    try {
      const preview = await readFileAsDataUrl(file);
      setEditingRestaurantForm((prev) => ({
        ...prev,
        imagePreview: preview,
        imageFile: file,
      }));
      setAlmacenFeedback({ type: "", message: "" });
    } catch (error) {
      toast.error(error?.message || "No se pudo preparar la imagen del almacén.");
    }

    event.target.value = "";
  };

  const handleSettingsTabChange = (tabId) => {
    setActiveTab(tabId);
    const query = new URLSearchParams(location.search);
    query.set("tab", "ajustes");
    if (tabId === "cuenta") {
      query.delete("settingsTab");
    } else {
      query.set("settingsTab", tabId);
    }
    history.replace({ pathname: location.pathname, search: query.toString() });
  };

  const handleSaveProfile = async () => {
    if (isSavingProfile) return;

    if (isProfileInvalid) {
      setProfileFeedback({ type: "error", message: "Revisa los campos marcados antes de guardar." });
      return;
    }

    const payload = {
      firstName: accountForm.name.trim(),
      lastName: accountForm.lastName.trim(),
      email: accountForm.email.trim(),
    };

    setIsSavingProfile(true);
    setProfileFeedback({ type: "", message: "" });
    try {
      await updateProfile(payload);
      setProfileFeedback({ type: "success", message: "Perfil actualizado correctamente." });
      toast.success("Datos de la cuenta actualizados.");
    } catch (error) {
      setProfileFeedback({ type: "error", message: error?.message || "No se pudo actualizar el perfil." });
      toast.error(error?.message || "No se pudo actualizar la cuenta.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveRestaurant = async () => {
    if (isSavingRestaurant) return;
    if (!editingRestaurantForm.id || isAlmacenInvalid) {
      setAlmacenFeedback({ type: "error", message: "Revisa los campos marcados antes de guardar." });
      return;
    }

    const payload = new FormData();
    payload.append("name", editingRestaurantForm.name.trim());
    payload.append("address", editingRestaurantForm.address.trim());
    payload.append("country", editingRestaurantForm.country.trim());
    if (editingRestaurantForm.placeId) {
      payload.append("place_id", editingRestaurantForm.placeId);
    }
    if (editingRestaurantForm.imageFile) {
      payload.append("file", editingRestaurantForm.imageFile);
    }

    setIsSavingRestaurant(true);
    setAlmacenFeedback({ type: "", message: "" });
    try {
      await updateTenantRestaurant(editingRestaurantForm.id, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reloadOwnerRestaurants(true);
      if (selectedOwnerRestaurant?._id === editingRestaurantForm.id || selectedOwnerRestaurant?.id === editingRestaurantForm.id) {
        setSelectedOwnerRestaurantId(editingRestaurantForm.id);
      }
      setAlmacenFeedback({ type: "success", message: "Perfil del almacén actualizado correctamente." });
      toast.success("Almacén actualizado correctamente.");
    } catch (error) {
      setAlmacenFeedback({ type: "error", message: error?.message || "No se pudo actualizar el almacén." });
      toast.error(error?.message || "No se pudo actualizar el almacén.");
    } finally {
      setIsSavingRestaurant(false);
    }
  };

  return (
    <>
      <DesktopOnlyBanner>
        <DesktopPageHeader img={avatar} alt={restoName} />
      </DesktopOnlyBanner>
      <Wrapper>
              <DesktopOnlyHeaderBlock>
                <HeaderTitle>Ajustes de cuenta</HeaderTitle>
                <HeaderSubtitle>
                  {[accountForm.name, accountForm.lastName].filter(Boolean).join(" ") || "Nombre del usuario"}
                </HeaderSubtitle>
              </DesktopOnlyHeaderBlock>

            <Tabs>
              {SETTINGS_TABS.map((tab) => (
                <TabButton
                  key={tab.id}
                  type="button"
                  $active={activeTab === tab.id}
                  onClick={() => handleSettingsTabChange(tab.id)}
                >
                  {tab.label}
                </TabButton>
              ))}
            </Tabs>

            {activeTab === "cuenta" && (
              <AccountSection>
                <AccountTitle>Perfil del usuario</AccountTitle>
                <Divider />

                <AvatarRow>
                  <AvatarPreview>
                    {profileAvatar ? <img src={profileAvatar} alt={accountForm.name || "Usuario"} /> : <span>Sin foto</span>}
                  </AvatarPreview>
                  <div>
                    <UploadButton as="label" htmlFor="owner-account-avatar">
                      {isUploadingAvatar ? "Subiendo..." : "Cambiar foto"}
                    </UploadButton>
                    <HiddenInput
                      id="owner-account-avatar"
                      type="file"
                      accept={IMAGE_UPLOAD_ACCEPT}
                      onChange={handleAvatarChange}
                      disabled={isUploadingAvatar}
                    />
                  </div>
                </AvatarRow>

                {isLoadingProfile && <MutedText>Cargando datos del usuario...</MutedText>}
                {profileFeedback.message && (
                  <InlineFeedback $type={profileFeedback.type}>{profileFeedback.message}</InlineFeedback>
                )}

                <FieldsGrid>
                  <Field>
                    <label htmlFor="owner-name">Nombre</label>
                    <input
                      id="owner-name"
                      placeholder="Ej: Juan"
                      value={accountForm.name}
                      onChange={(event) => handleFormChange("name", event.target.value)}
                      disabled={isLoadingProfile || isSavingProfile}
                      aria-invalid={Boolean(profileErrors.name)}
                    />
                    {!isLoadingProfile && profileErrors.name && <FieldError>{profileErrors.name}</FieldError>}
                  </Field>
                  <Field>
                    <label htmlFor="owner-lastname">Apellidos</label>
                    <input
                      id="owner-lastname"
                      placeholder="Ej: Pérez Soto"
                      value={accountForm.lastName}
                      onChange={(event) => handleFormChange("lastName", event.target.value)}
                      disabled={isLoadingProfile || isSavingProfile}
                      aria-invalid={Boolean(profileErrors.lastName)}
                    />
                    {!isLoadingProfile && profileErrors.lastName && <FieldError>{profileErrors.lastName}</FieldError>}
                  </Field>
                  <Field>
                    <label htmlFor="owner-email">Email</label>
                    <input
                      id="owner-email"
                      type="email"
                      placeholder="Ej: juan@empresa.com"
                      value={accountForm.email}
                      readOnly
                      disabled
                      aria-invalid={Boolean(profileErrors.email)}
                    />
                    {!isLoadingProfile && profileErrors.email && <FieldError>{profileErrors.email}</FieldError>}
                  </Field>
                </FieldsGrid>

                <SaveButton
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={isLoadingProfile || isSavingProfile || isProfileInvalid}
                >
                  {isSavingProfile ? "Guardando..." : "Guardar"}
                </SaveButton>
              </AccountSection>
            )}

            {activeTab === "almacen" && (
              <AccountSection>
                <SectionHeader>
                  <SectionTitle>Mi Almacén</SectionTitle>
                </SectionHeader>
                <Divider />
                {almacenFeedback.message && (
                  <InlineFeedback $type={almacenFeedback.type}>{almacenFeedback.message}</InlineFeedback>
                )}
                {ownerRestaurantsLoading ? (
                  <MutedText>Cargando almacén...</MutedText>
                ) : !currentAlmacen ? (
                  <MutedText>No hay almacén asociado.</MutedText>
                ) : (
                  <>
                    <AvatarRow>
                      <AvatarPreview>
                        {editingRestaurantForm.imagePreview ? (
                          <img src={editingRestaurantForm.imagePreview} alt={editingRestaurantForm.name || "almacén"} />
                        ) : (
                          <span>Sin foto</span>
                        )}
                      </AvatarPreview>
                      <div>
                        <UploadButton as="label" htmlFor="owner-almacen-avatar">
                          Cambiar logo
                        </UploadButton>
                        <HiddenInput
                          id="owner-almacen-avatar"
                          type="file"
                          accept={IMAGE_UPLOAD_ACCEPT}
                          onChange={handleEditRestaurantImageChange}
                          disabled={isSavingRestaurant}
                        />
                      </div>
                    </AvatarRow>
                    <FieldsGrid>
                      <Field>
                        <label htmlFor="owner-almacen-name">Nombre</label>
                        <input
                          id="owner-almacen-name"
                          value={editingRestaurantForm.name}
                          onChange={(event) => handleEditRestaurantChange("name", event.target.value)}
                          placeholder="Ej: Almacén Central"
                          disabled={isSavingRestaurant}
                          aria-invalid={Boolean(almacenErrors.name)}
                        />
                        {almacenErrors.name && <FieldError>{almacenErrors.name}</FieldError>}
                      </Field>
                      <Field>
                        <label htmlFor="owner-almacen-address">Dirección</label>
                        <input
                          id="owner-almacen-address"
                          value={editingRestaurantForm.address || "Sin dirección"}
                          readOnly
                          disabled
                        />
                      </Field>
                      <Field>
                        <label htmlFor="owner-almacen-country">País</label>
                        <input
                          id="owner-almacen-country"
                          value={editingRestaurantForm.country || "Sin país"}
                          readOnly
                          disabled
                        />
                      </Field>
                    </FieldsGrid>
                    <SaveButton
                      type="button"
                      onClick={handleSaveRestaurant}
                      disabled={isSavingRestaurant || isAlmacenInvalid || !editingRestaurantForm.id}
                    >
                      {isSavingRestaurant ? "Guardando..." : "Guardar"}
                    </SaveButton>
                  </>
                )}
              </AccountSection>
            )}

      </Wrapper>
    </>
  );
}

const Wrapper = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0 90px;
  display: grid;
  gap: 16px;

  @media (max-width: 1024px) {
    padding: 16px 0 90px;
  }
`;

const HeaderBlock = styled.div`
  display: grid;
  gap: 4px;
`;

const DesktopOnlyHeaderBlock = styled(HeaderBlock)`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const DesktopOnlyBanner = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #111827;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #ddd;
  margin-bottom: 1rem;
  gap: 2rem;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  padding: 0.5rem 0;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "#000" : "#666")};
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ $active }) => ($active ? "#000" : "transparent")};
  }
`;

const AccountSection = styled.section`
  display: grid;
  gap: 14px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #111827;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const AccountTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #111827;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #e5e7eb;
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AvatarPreview = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #dbeafe;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #60a5fa;
  font-size: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadButton = styled.button`
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #111827;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FieldsGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 6px;

  label {
    font-size: 13px;
    color: #6b7280;
  }

  input,
  select {
    height: 46px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 0 12px;
    font-size: 14px;
    color: #111827;
    outline: none;
    background: #fff;

    &::placeholder {
      color: #9ca3af;
    }

    &[aria-invalid="true"] {
      border-color: #dc2626;
      background: #fff7f7;
    }

    @media (max-width: 768px) {
      height: 42px;
    }
  }
`;

const FieldError = styled.p`
  margin: 0;
  color: #dc2626;
  font-size: 12px;
`;

const InlineFeedback = styled.p`
  margin: 0;
  color: ${({ $type }) => ($type === "success" ? "#15803d" : "#b91c1c")};
  background: ${({ $type }) => ($type === "success" ? "#f0fdf4" : "#fef2f2")};
  border: 1px solid ${({ $type }) => ($type === "success" ? "#bbf7d0" : "#fecaca")};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
`;

const SaveButton = styled.button`
  border: none;
  border-radius: 999px;
  background: #16a34a;
  color: #fff;
  font-weight: 600;
  padding: 12px 22px;
  width: fit-content;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MutedText = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
`;
